/**
 * Contact modal triggers
 * -----------------------------------------------------------------------------
 * Opens a "contact-modal" section when a link pointing to its trigger anchor is
 * clicked (for instance a button whose URL is "#business-contact").
 *
 * This file is only loaded by the "contact-modal" section, but it is written so
 * that it stays completely inert when no modal is present on the page: every
 * lookup is guarded and the click listener bails out early when the clicked
 * element does not resolve to an existing contact modal.
 *
 * The modal itself is a standard theme <x-modal> element, so closing (close
 * cross, click outside, escape key), focus trapping and scroll locking are all
 * handled by the theme.
 */
(function () {
  'use strict';

  var MODAL_ATTRIBUTE = 'data-contact-modal';
  var TRIGGER_ATTRIBUTE = 'data-contact-modal-trigger';

  // The listener lives on the document, so it only ever needs to be attached once,
  // even if several contact modal sections (and therefore several copies of this
  // script) are present on the page.
  if (window.themeContactModalTriggersLoaded) {
    return;
  }

  window.themeContactModalTriggersLoaded = true;

  /**
   * Normalize a value into a bare element ID ("#business-contact" -> "business-contact")
   */
  function normalizeId(value) {
    if (!value) {
      return '';
    }

    var id = String(value).trim().replace(/^#/, '');

    try {
      id = decodeURIComponent(id);
    } catch (error) {
      // Malformed URI sequence: keep the raw value
    }

    return id;
  }

  /**
   * Find the contact modal matching the given ID. Returns null when the modal does not
   * exist on the current page, or when the element is not one of our modals
   */
  function findModal(id) {
    if (!id) {
      return null;
    }

    var element = document.getElementById(id);

    if (!element || !element.hasAttribute || !element.hasAttribute(MODAL_ATTRIBUTE)) {
      return null;
    }

    return element;
  }

  /**
   * Open a modal. The custom element may not have been upgraded yet (theme.js is
   * loaded as a module), in which case we wait for the definition before opening
   */
  function openModal(modal) {
    if (!modal) {
      return;
    }

    if (typeof modal.show === 'function') {
      modal.show();
      return;
    }

    if (!window.customElements || typeof window.customElements.whenDefined !== 'function') {
      return;
    }

    window.customElements
      .whenDefined('x-modal')
      .then(function () {
        if (typeof window.customElements.upgrade === 'function') {
          window.customElements.upgrade(modal);
        }

        if (typeof modal.show === 'function') {
          modal.show();
        }
      })
      .catch(function () {
        // The element was never defined: nothing to open
      });
  }

  /**
   * Extract the trigger ID a given element points to (empty string when it is not a trigger)
   */
  function getTriggerId(element) {
    if (element.hasAttribute(TRIGGER_ATTRIBUTE)) {
      return normalizeId(element.getAttribute(TRIGGER_ATTRIBUTE));
    }

    var href = element.getAttribute('href');

    if (!href) {
      return '';
    }

    // Plain anchor link ("#business-contact")
    if (href.charAt(0) === '#') {
      return normalizeId(href);
    }

    // Absolute or root-relative URL: only treat it as a trigger when it targets the
    // current page, so that cross-page links keep navigating normally
    var url;

    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return '';
    }

    if (!url.hash || url.hash === '#') {
      return '';
    }

    if (url.host !== window.location.host || url.pathname !== window.location.pathname) {
      return '';
    }

    return normalizeId(url.hash);
  }

  /**
   * The theme delegates its own click handler for 'a[href*="#"]' on <html> (see the IIFE at the
   * bottom of theme.js): it smooth-scrolls to the anchor and calls preventDefault(). Because <html>
   * is a descendant of the document, that handler runs before a bubble-phase listener registered
   * here and swallows the click.
   *
   * Listening on the capture phase of the document puts this handler first in line, and stopping
   * propagation once a modal has been matched keeps the theme from scrolling to an element that is
   * display:none until it opens.
   */
  document.addEventListener('click', function (event) {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var target = event.target;

    // The event target can be a text node when the click happens on a label text
    if (target && target.nodeType !== 1) {
      target = target.parentElement;
    }

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    var trigger = target.closest('a[href], [' + TRIGGER_ATTRIBUTE + ']');

    if (!trigger) {
      return;
    }

    // Let links that open in another tab/window behave normally
    if (trigger.target && trigger.target !== '_self') {
      return;
    }

    var modal = findModal(getTriggerId(trigger));

    if (!modal) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openModal(modal);
  }, true);

  /**
   * Allow the modal to be opened from another page (ie. "/pages/about#business-contact")
   */
  function openFromHash() {
    openModal(findModal(normalizeId(window.location.hash)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHash, { once: true });
  } else {
    openFromHash();
  }

  window.addEventListener('hashchange', openFromHash);

  /**
   * Anchor clicks the theme handles itself never produce a real hash change: theme.js dispatches
   * this custom event instead. Mirroring it here is a safety net for any trigger that reaches the
   * theme's handler before ours.
   */
  document.documentElement.addEventListener('hashchange:simulate', function (event) {
    if (!event.detail || !event.detail.hash) {
      return;
    }

    openModal(findModal(normalizeId(event.detail.hash)));
  });
})();
